import LoaderComponent from "component/LoaderComponent";
import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useLocation, useParams} from "react-router";
import {Button, Form, Icon, Radio} from "semantic-ui-react";
import axios from "service/http";
import {getEmployeeTokenData, registerRapidTestkitResult} from "service/testKitServices";
import styled from "styled-components";
import {EmployeeTokenDataDto} from "ts-types/api.types";
import {isoToDisplayDate} from "util/dateUtils";
import {errorUtils} from "util/errorUtils";

const StyledContainer = styled.div`
  display: flex;
  align-items: start;
  justify-content: center;
  flex-direction: column;
  max-width: 768px;

  .info-text {
    font-size: 1.2rem;
    margin-bottom: 2rem;
  }

  video {
    border: 2px solid;
  }

  .snapshot-container {
    display: flex;
    flex-direction: column;
    align-self: center;
    align-items: center;

    .image-input {
      display: none;
    }

    .snapshot-placeholder {
      display: flex;
      flex-direction: column;
      max-width: 80vh;
      min-width: 30vh;
      min-height: 20vh;
      align-items: center;
      border: 1px solid #e5e5e5;
      padding-top: 1rem;

      i.icon {
        color: #e5e5e5;
      }
    }

    .take-snapshot-action {
      display: flex;
      flex-direction: column;
      margin-top: 1rem;
      align-items: center;

      i.icon {
        color: #768aff;
      }

      .action-label {
        color: #293569;
        font-size: 1.1rem;
      }
    }

    .image-box {
      max-width: 90%;
      max-height: 50vh;
      margin: 10px;
      text-align: center;

      img {
        height: inherit;
        max-height: inherit;
        max-width: inherit;
      }
    }
  }

  .radio-box {
    margin: 2rem 0;

    .field-label {
      margin-right: 2rem;
    }

    .field {
      display: inline-block;
      margin-right: 1.5rem;
    }
  }

  .result {
    margin: 1rem 0 2.5rem;
    font-size: 1.2rem;
    font-weight: 500;
  }

  .warning {
    background-color: #f3e9ba;
    padding: 1rem;
    font-size: 1.5rem;
    margin-bottom: 2rem;
    width: 100%;
  }

  .success {
    background-color: #BBF3BA;
    padding: 1rem;
    font-size: 1.5rem;
    margin-bottom: 2rem;
    width: 100%;
  }

  .error {
    background-color: #FFCCCC;
    padding: 1rem;
    font-size: 1.5rem;
    margin-bottom: 2rem;
    width: 100%;
    white-space: pre-line;
  }

  .info-message {
    margin: 1rem 0;
    font-size: 1.2rem;
  }
`;

interface InfoRowProps {
  backgroundColor?: string;
}

const StyledInfoRow = styled.div`
  background-color: ${(props: InfoRowProps) => props.backgroundColor || 'inherited'};
  width: 100%;
  display: flex;
  padding: 0.5rem;

  *:first-child {
    width: 150px;
  }

  *:nth-child(2) {
    flex: 1;
  }

  &.target-employee {
    margin-bottom: 1.5rem;
  }
`;

const StyledButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;

  .ui.button {
    margin-right: 0.75rem;
  }
`;

interface TestResultImageUploadParams {
  token: string;
}

type PageState = "ENTRY" | "SUCCESS" | "ERROR" | "FILE_SIZE_EXCEEDED";

function useQuery() {
  const {search} = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
}

const TestResultImageUpload = () => {

  const {t} = useTranslation('login');
  const {token} = useParams<TestResultImageUploadParams>();

  const query = useQuery();
  const testkitOrderId = query.get("testkitOrderId");

  const [cancelTokenSource/*, setCancelTokenSource*/] = React.useState(axios.CancelToken.source());
  const [pageState, setPageState] = React.useState("ENTRY" as PageState);
  const [officialCertificateWarning, setOfficialCertificateWarning] = React.useState(false);
  const [tokenData, setTokenData] = React.useState({} as Partial<EmployeeTokenDataDto>);
  const [errorMessage, setErrorMessage] = useState(undefined as string | undefined);

  useEffect(() => {
    getEmployeeTokenData(token, cancelTokenSource)
    .then(response => {
      setTokenData(response);
    });
    // eslint-disable-next-line
  }, []);

  const [testResult, setTestResult] = useState("");
  const [testResultSnapshot, setTestResultSnapshot] = useState(undefined);
  const [source, setSource] = useState("");
  const [imageUploading, setImageUploading] = useState(false);
  const [disableUploadButton, setDisableUploadButton] = useState(false);

  const handleCapture = (target: any) => {
    if (target.files) {
      if (target.files.length !== 0) {
        const file = target.files[0];

        if (file.size <= 10485760) {
          setTestResultSnapshot(file);
          const newUrl = URL.createObjectURL(file);
          setSource(newUrl);
        } else {
          setPageState("FILE_SIZE_EXCEEDED");
        }
      }
    }
  };

  const onUploadTestResult = async () => {
    setImageUploading(true);
    setDisableUploadButton(true);
    try {
      const responseDto = await registerRapidTestkitResult(
          token, testkitOrderId, testResult, testResultSnapshot, tokenData.sourceEmployeeMandateId, cancelTokenSource);

      if (testResult === 'NEGATIVE'
          && responseDto.automaticCertificateNotification
          && !responseDto.officialCertificateGenerated) {

        setOfficialCertificateWarning(true);
      }

      setPageState("SUCCESS");
    } catch (e) {
      if (e.message === "Network Error") {
        setPageState("FILE_SIZE_EXCEEDED");
      } else {
        handleError(e.response.data);
        setPageState("ERROR");
      }
    }
    setImageUploading(false);
    setDisableUploadButton(false);
  };

  const handleError = (exception: any) => {

    const error = exception;
    if (error) {
      const errorCode = error.errorCode;
      const knownErrors: Array<string> = [
        errorUtils.incompleteDataProvided
      ];

      const violations: Array<any> = error.violations;

      if (violations && violations.length > 0) {
        violations.forEach(violation => {
          if (knownErrors.includes(violation.errorCode)) {
            setErrorMessage(t(`rapidTestkitSnapshot.message.${violation.errorCode}`));
          }
        });
      } else {
        setErrorMessage(t('rapidTestkitSnapshot.message.somethingWentWrong'));
      }
    }
  };

  const takeSnapshotKey = source
      ? "rapidTestkitSnapshot.action.retakeSnaphot"
      : "rapidTestkitSnapshot.action.takeSnaphot";

  const renderTopPageData = () => <>
    <StyledInfoRow backgroundColor="#e5e5e5">
      <span>{t("testkitToken.content.institution")}</span>
      <span><b>{tokenData.companyName}, {tokenData.companyZip} {tokenData.companyCity}</b></span>
    </StyledInfoRow>
    <StyledInfoRow>
      <span>{t("testkitToken.content.administrator")}</span>
      <span>
                    <b>
                        {`${tokenData.sourceEmployeeFirstName} ${tokenData.sourceEmployeeLastName}`}, {`${tokenData.sourceEmployeeEmail}`}
                    </b>
                </span>
    </StyledInfoRow>

    <h1>{t("rapidTestkitSnapshot.title")}</h1>

    <StyledInfoRow className="target-employee" backgroundColor="#e5e5e5">
      <span>{t("testkitToken.content.employee")}</span>
      <span><b>{`${tokenData.targetEmployeeFirstName} ${tokenData.targetEmployeeLastName}`}, {`${isoToDisplayDate(tokenData.targetEmployeeBirthDate)}`}</b></span>
    </StyledInfoRow>
  </>;

  const renderEntryState = () => <>
    {imageUploading && <LoaderComponent message={t("testResult.uploadImage.spinner")} />}
    {renderTopPageData()}

    <div className="snapshot-container">
      <input
          accept="image/*"
          className="image-input"
          id="icon-button-file"
          type="file"
          capture="environment"
          onChange={(e) => handleCapture(e.target)}
      />

      <label htmlFor="icon-button-file">
        {
          source
              ?
              <div className="image-box">
                <img src={source} alt={"snap"} className="" />
              </div>
              :
              <div className="snapshot-placeholder">
                <Icon.Group size="huge">
                  <Icon name="flask" />
                  <Icon corner="top right" name="address card outline" />
                </Icon.Group>
              </div>
        }
      </label>

      <label htmlFor="icon-button-file">
        <div className="take-snapshot-action">
          <Icon name="camera" size="large" />
          <div className="action-label">
            {t(takeSnapshotKey)}
          </div>
        </div>
      </label>
    </div>

    <div className="radio-box">
      <label htmlFor="testResult" className="field-label">
        {t("rapidTestkitSnapshot.form.testResult")}
      </label>
      <Form.Field>
        <Radio
            label={t("testResult.POSITIVE")}
            name="testResult"
            value="POSITIVE"
            checked={testResult === 'POSITIVE'}
            onChange={(e, data) => setTestResult(data.value as string)}
        />
      </Form.Field>
      <Form.Field>
        <Radio
            label={t("testResult.NEGATIVE")}
            name="testResult"
            value="NEGATIVE"
            checked={testResult === 'NEGATIVE'}
            onChange={(e, data) => setTestResult(data.value as string)}
        />
      </Form.Field>
      <Form.Field>
        <Radio
            label={t("testResult.UNCERTAIN")}
            name="testResult"
            value="UNCERTAIN"
            checked={testResult === 'UNCERTAIN'}
            onChange={(e, data) => setTestResult(data.value as string)}
        />
      </Form.Field>
    </div>

    <StyledButtonContainer>
      <Button
          type="button"
          className="action-button"
          primary
          onClick={onUploadTestResult}
          disabled={!testResult || !testResultSnapshot || disableUploadButton}
      >
        {t('testkitToken.action.register')}
      </Button>
    </StyledButtonContainer>
  </>;

  const renderSuccess = () => <>
    <div className="success">
      {t("rapidTestkitSnapshot.message.success")}
    </div>
    {
        officialCertificateWarning &&
      <div className="warning">
        {t("rapidTestkitSnapshot.message.warn.officialCertificateNotGenerated")}
      </div>
    }
    {renderTopPageData()}
    <div className="info-message">
      {t("rapidTestkitSnapshot.message.backToWeb")}
    </div>
  </>;

  const renderError = () => <>
    {errorMessage &&
      <div className="error">
        {errorMessage}
      </div>
    }

    {renderTopPageData()}

    <div className="info-message">
      {t("rapidTestkitSnapshot.message.somethingWentWrong")}
    </div>

    <StyledButtonContainer>
      <Button
          type="button"
          className="action-button"
          color="grey"
          onClick={() => setPageState("ENTRY")}
      >
        {t('rapidTestkitSnapshot.action.registerAgain')}
      </Button>
    </StyledButtonContainer>
  </>;

  const renderFileSizeExceeded = () => <>
    <div className="error">
      {t("rapidTestkitSnapshot.message.fileSizeExceeded")}
    </div>
    {renderTopPageData()}

    <div className="info-message">
      {t("rapidTestkitSnapshot.message.smallerImage")}
    </div>

    <StyledButtonContainer>
      <Button
          type="button"
          className="action-button"
          color="grey"
          onClick={() => setPageState("ENTRY")}
      >
        {t('rapidTestkitSnapshot.action.registerAgain')}
      </Button>
    </StyledButtonContainer>
  </>;


  return <StyledContainer>
    {
        pageState === "ENTRY" &&
        renderEntryState()
    }
    {
        pageState === "SUCCESS" &&
        renderSuccess()
    }
    {
        pageState === "ERROR" &&
        renderError()
    }
    {
        pageState === "FILE_SIZE_EXCEEDED" &&
        renderFileSizeExceeded()
    }
  </StyledContainer>;
};

export default TestResultImageUpload;
